import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { X, Download, ArrowRight, ExternalLink } from 'lucide-react';
import PlanAnalysisReport from './PlanAnalysisReport';
import NextStepsTab from './NextStepsTab';
import { API_BASE_URL } from '../services/api';

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, LineElement, Title, ChartTooltip, ChartLegend);

const FORCE_REBUILD = "2025-12-30-16:00";

// Short currency format for axis labels (no decimals)
const formatCurrencyShort = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const BUILD_TIME="Tue 12/30/2025-15:59:25.30"; const BUILD_VERSION = "v1.0.1"; const PlanAnalysisScroll = () => {
  const { ein, planNumber, planYear } = useParams();
  const [planData, setPlanData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [historyData, setHistoryData] = useState([]); // For line chart
  const [rawHistoryData, setRawHistoryData] = useState([]); // For modal table
  const [scatterData, setScatterData] = useState(null);
  const [scenarioData, setScenarioData] = useState(null);
  const [contributionCostData, setContributionCostData] = useState([]);
  const [lookbackData, setLookbackData] = useState(null);
  // Corrected peer-median lookback (replaces the broken Phase 5
  // total_economic_benefit headline that was rendering $22M for Mohawk).
  const [peerLookbackData, setPeerLookbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // STEP 1: Fetch critical data first (plan details) - FAST
        console.log('🎯 NEW VERSION LOADING - Feb 7 2026 5:45 PM');
        const details = await fetch(`${API_BASE_URL}/plan/${ein}/${planNumber}/${planYear}/details`).then(r => r.json());
        setPlanData(details);
        
        // Show page immediately with basic plan info
        setLoading(false);
        
        // STEP 2: Fetch fast endpoints in parallel - they'll appear as they complete
        
        // Scatter plot (now fast with new index)
        console.log('🚀 FETCHING SCATTER PLOT NOW!');
        const scatterUrl = `${API_BASE_URL}/scatter/${ein}/${planNumber}`;
        console.log('📍 Scatter URL:', scatterUrl);
        fetch(scatterUrl)
          .then(r => {
            console.log('📦 Scatter response received:', r.status);
            return r.json();
          })
          .then(scatter => {
            console.log('✅ Scatter data parsed:', scatter);
            // Transform scatter data
            let transformedScatter = null;
            if (scatter && typeof scatter === 'object') {
              const filteredPeers = scatter.peer_companies?.filter(peer => 
                peer.cost_per_participant >= 0
              ) || [];
              
              // Chart.js can handle all points - no need to sample!
              console.log(`📊 Loading ${filteredPeers.length} peers for chart`);
              
              transformedScatter = {
                ...scatter,
                peer_companies: filteredPeers.map(peer => ({
                  ...peer,
                  participants_with_balance: peer.participants,
                  name: peer.sponsor_name,
                  assets: 0
                })),
                target_company: scatter.target_company ? {
                  ...scatter.target_company,
                  participants_with_balance: scatter.target_company.participants,
                  name: scatter.target_company.sponsor_name,
                  assets: 0
                } : null
              };
            }
            setScatterData(transformedScatter);
          })
          .catch(err => console.error('Error loading scatter:', err));
        
        // History data (fast) - for modal table
        fetch(`${API_BASE_URL}/history/${ein}/${planNumber}`)
          .then(r => r.json())
          .then(history => {
            setRawHistoryData(history);
          })
          .catch(err => console.error('Error loading history:', err));
        
        // Scenarios (fast)
        fetch(`${API_BASE_URL}/scenarios/${ein}/${planNumber}`)
          .then(r => r.json())
          .then(scenarios => setScenarioData(scenarios))
          .catch(err => console.error('Error loading scenarios:', err));
        
        // Lookback (fast)
        fetch(`${API_BASE_URL}/lookback/${ein}/${planNumber}`)
          .then(r => r.json())
          .then(lookback => setLookbackData(lookback))
          .catch(() => setLookbackData(null));

        // Peer-median lookback — the corrected economic-impact figures for the
        // "Historical Impact Analysis" headline + the per-participant text.
        fetch(`${API_BASE_URL}/peer-lookback/${ein}/${planNumber}`)
          .then(r => (r.ok ? r.json() : null))
          .then(peerLookback => setPeerLookbackData(peerLookback))
          .catch(() => setPeerLookbackData(null));
        
        // STEP 3: Fetch SLOW comparison endpoint (2.5 min) - page already visible
        fetch(`${API_BASE_URL}/comparison/${ein}/${planNumber}`)
          .then(r => r.json())
          .then(comparison => {
            setComparisonData(comparison);
            
            // Transform comparison data for line chart (depends on comparison)
            const transformedHistory = Array.isArray(comparison) ? 
              [...comparison].reverse().map(item => ({
                plan_year: item.plan_year,
                company_cost_per_participant: item.company_cost_per_participant,
                peer_avg_cost_per_participant: item.peer_avg_cost_per_participant,
                peer_median_cost_per_participant: item.peer_median_cost_per_participant
              })) : [];
            setHistoryData(transformedHistory);
          })
          .catch(err => console.error('Error loading comparison:', err));
        
        // Try to fetch contribution cost data (optional)
        fetch(`${API_BASE_URL}/contribution-cost-analysis/${ein}/${planNumber}`)
          .then(response => {
            if (response.ok) {
              return response.json();
            }
            throw new Error('Contribution cost endpoint not available');
          })
          .then(contributionCost => {
            setContributionCostData(contributionCost);
          })
          .catch(() => {
            console.log('Contribution cost data not available, will calculate from history data');
            // Calculate from already-fetched history data
            setTimeout(() => {
              if (rawHistoryData && Array.isArray(rawHistoryData) && rawHistoryData.length > 0) {
                const calculated = rawHistoryData.map(year => {
                  const adminExpenses = year.total_admin_expenses || year.admin_expenses || 0;
                  const contributions = year.total_contributions || year.contributions || 0;
                  const assets = year.total_assets || year.assets || 0;
                  
                  return {
                    plan_year: year.plan_year || year.year,
                    admin_cost_as_pct_of_total_contrib: contributions > 0 
                      ? (adminExpenses / contributions * 100) 
                      : 0,
                    admin_cost_as_pct_of_assets: assets > 0 
                      ? (adminExpenses / assets * 100) 
                      : 0
                  };
                });
                setContributionCostData(calculated);
              }
            }, 100); // Wait briefly for rawHistoryData to be set
          });

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchAllData();
  }, [ein, planNumber, planYear]);

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyDetailed = (value) => {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatCompact = (value) => {
    if (!value && value !== 0) return 'N/A';
    if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + 'M';
    }
    if (value >= 1000) {
      return '$' + (value / 1000).toFixed(0) + 'K';
    }
    return formatCurrency(value);
  };

  const toProperCase = (text) => {
    if (!text) return '';
    return text.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading plan analysis...</div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>No data available for this plan</h2>
      </div>
    );
  }

  // Check if comparison data is still loading (it loads async after planData)
  const comparisonLoading = !comparisonData || comparisonData.length === 0;

  // Get most recent comparison year (safely handle loading state)
  const latestComparison = comparisonData?.[0];
  
  // If still loading comparison data, show a loading indicator for that section
  if (comparisonLoading) {
    return (
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: '2rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        textAlign: 'center'
      }}>
        <h2>{planData?.sponsor_name || 'Plan'}</h2>
        <p style={{ color: '#666', marginTop: '1rem' }}>Loading comparison data...</p>
      </div>
    );
  }
  
  // CRITICAL FIX: Check if latestComparison has the required data
  // Some plans (especially old/terminated ones) may not have comparison metrics
  if (!latestComparison || !latestComparison.company_cost_per_participant) {
    const planYearFromData = planData?.plan_year || planYear || 'unknown';
    const dataAge = planData?.plan_year ? new Date().getFullYear() - planData.plan_year : null;
    
    return (
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: '2rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {/* Warning Alert */}
        <div style={{
          background: '#FEF3C7',
          border: '2px solid #F59E0B',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
            <div style={{ fontSize: '2rem' }}>⚠️</div>
            <div>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: 700, 
                color: '#92400E',
                marginBottom: '0.5rem'
              }}>
                Limited Data Available
              </h3>
              <p style={{ fontSize: '0.9375rem', color: '#78350F', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                The most recent filing for <strong>{planData?.sponsor_name || 'this plan'}</strong> is 
                from <strong>{planYearFromData}</strong>
                {dataAge && ` (${dataAge} years old)`}.
              </p>
              <p style={{ fontSize: '0.9375rem', color: '#78350F', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                Cost comparison data is not available for plans with filings this old. 
                This typically means the plan is:
              </p>
              <ul style={{ 
                fontSize: '0.9375rem', 
                color: '#78350F', 
                lineHeight: 1.6,
                marginLeft: '1.5rem',
                marginBottom: '0.75rem'
              }}>
                <li>Terminated or no longer active</li>
                <li>Merged with another plan</li>
                <li>Below the filing threshold (fewer than 100 participants)</li>
                <li>Using a different EIN or plan number</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Available Plan Information Card */}
        <div style={{
          background: 'white',
          border: '2px solid #E5E7EB',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h4 style={{ 
            fontSize: '1.125rem', 
            fontWeight: 700, 
            color: '#111827',
            marginBottom: '1rem'
          }}>
            Available Plan Information
          </h4>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '1rem',
            fontSize: '0.9375rem'
          }}>
            <div>
              <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Plan Name</p>
              <p style={{ color: '#111827', fontWeight: 600 }}>
                {planData?.plan_name || 'N/A'}
              </p>
            </div>
            
            <div>
              <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Sponsor Name</p>
              <p style={{ color: '#111827', fontWeight: 600 }}>
                {planData?.sponsor_name || 'N/A'}
              </p>
            </div>
            
            <div>
              <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>EIN</p>
              <p style={{ color: '#111827', fontWeight: 600 }}>
                {planData?.ein || ein}
              </p>
            </div>
            
            <div>
              <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Plan Number</p>
              <p style={{ color: '#111827', fontWeight: 600 }}>
                {planData?.plan_number || planNumber}
              </p>
            </div>
            
            <div>
              <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Last Filing Year</p>
              <p style={{ color: '#111827', fontWeight: 600 }}>
                {planYearFromData}
              </p>
            </div>
            
            <div>
              <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Data Status</p>
              <p style={{ 
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                background: dataAge > 3 ? '#FEE2E2' : dataAge > 1 ? '#FEF3C7' : '#D1FAE5',
                color: dataAge > 3 ? '#991B1B' : dataAge > 1 ? '#92400E' : '#065F46'
              }}>
                {dataAge === 0 ? 'Current' : 
                 dataAge === 1 ? '1 year old' : 
                 dataAge ? `${dataAge} years old` : 'Unknown'}
              </p>
            </div>
          </div>
        </div>

        {/* What You Can Do Card */}
        <div style={{
          background: '#EFF6FF',
          border: '2px solid #3B82F6',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h4 style={{ 
            fontSize: '1.125rem', 
            fontWeight: 700, 
            color: '#1E40AF',
            marginBottom: '1rem'
          }}>
            What You Can Do
          </h4>
          
          <ul style={{ 
            fontSize: '0.9375rem', 
            color: '#1E3A8A', 
            lineHeight: 1.6,
            marginLeft: '1.5rem'
          }}>
            <li style={{ marginBottom: '0.5rem' }}>
              Contact the employer or plan sponsor to verify the plan's current status
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              Check if the plan has been renamed, merged with another plan, or continues under a different structure
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              Search for more recent filings under a different EIN or plan number
            </li>
            <li>
              If you believe this is an error, please contact us at{' '}
              <a href="mailto:support@loveyour401k.com" style={{ color: '#1E40AF', fontWeight: 600 }}>
                support@loveyour401k.com
              </a>
            </li>
          </ul>
        </div>
      </div>
    );
  }
  
  // Check if other expenses were substituted for admin expenses (misfiled Form 5500)
  const hasExpenseSubstitution = planData?.effective_admin_expense_source === 'other_substituted';
  const substitutedYears = (rawHistoryData || [])
    .filter(y => y.effective_admin_expense_source === 'other_substituted')
    .map(y => y.plan_year)
    .sort();

  // NOW SAFE: latestComparison exists and has required data.
  // Benchmark = peer MEDIAN (locked decision, see PHASE5-FIX-HANDOFF.md).
  const costPerParticipant = latestComparison.company_cost_per_participant || 0;
  const peerMedian = latestComparison.peer_median_cost_per_participant || 0;
  const peerBenchmark = peerMedian;  // alias for label clarity
  const excessCost = costPerParticipant - peerBenchmark;
  const costRatio = peerBenchmark > 0 ? costPerParticipant / peerBenchmark : 0;

  // Determine if plan is lower cost or higher cost than peers
  const isLowerCost = excessCost < 0;
  const absCostDifference = Math.abs(excessCost);
  const costMultiplier = isLowerCost ? (peerBenchmark / costPerParticipant) : costRatio;

  // Historical economic impact comes from the corrected peer-median lookback
  // endpoint. The `headline` block already picks the best window (typically
  // MAX-history since 2009) and tells us whether to compound or fall back.
  let historicalLoss = 0;
  let historicalPerParticipant = 0;
  let headlineWindowLabel = null;   // e.g. "Since 2009" or "10-year"
  let lookbackDisplayMode = 'compounded';
  let lookbackFinalEoy = null;
  let lookbackFinalParticipants = null;

  if (peerLookbackData && peerLookbackData.headline) {
    const h = peerLookbackData.headline;
    lookbackDisplayMode = h.display_recommendation || 'compounded';
    headlineWindowLabel = h.label || null;
    lookbackFinalEoy = h.final_eoy ?? null;
    lookbackFinalParticipants = h.final_participants ?? null;

    // 'compounded'  → show total_economic_impact (compounded)
    // 'simple_sum'  → show simple_sum_overpayment (some years missing peer data)
    // 'hide'        → don't display the figure (data anomaly); we render 0
    if (lookbackDisplayMode === 'compounded') {
      historicalLoss = h.total_economic_impact || 0;
    } else if (lookbackDisplayMode === 'simple_sum') {
      historicalLoss = h.simple_sum_overpayment || 0;
    } else {
      historicalLoss = 0;
    }

    const ppts = lookbackFinalParticipants || planData.participants || 1;
    historicalPerParticipant = historicalLoss / ppts;

    console.log('📊 Historical Impact from peer-median lookback:', {
      window: h.window, label: h.label,
      total: h.total_economic_impact, simpleSum: h.simple_sum_overpayment,
      flag: h.data_quality_flag, mode: lookbackDisplayMode
    });
  } else if (Array.isArray(comparisonData)) {
    // Fallback if the peer-lookback endpoint is unavailable: simple-sum over
    // the comparison data using peer median per year. No compounding.
    comparisonData.forEach(year => {
      const median = year.peer_median_cost_per_participant;
      if (year.company_cost_per_participant && median && year.company_participants) {
        historicalLoss += (year.company_cost_per_participant - median) * year.company_participants;
      }
    });
    historicalPerParticipant = historicalLoss / (planData.participants || 1);
    console.log('📊 Historical Impact (simple-sum fallback vs peer median):', {
      totalLoss: historicalLoss, perParticipant: historicalPerParticipant
    });
  }

  // Calculate the earliest year for historical analysis
  // Use the LATER of: plan effective date OR first filing year in our database
  let historicalStartYear = 2009; // Default fallback
  
  try {
    // Get plan effective date year
    let planEffYear = null;
    if (planData?.plan_eff_date) {
      const effDate = new Date(planData.plan_eff_date);
      if (!isNaN(effDate.getTime())) {
        planEffYear = effDate.getFullYear();
      }
    }
    
    // Get first filing year from comparison data (comparisonData is sorted newest first, so last item is oldest)
    let firstFilingYear = null;
    if (comparisonData && comparisonData.length > 0) {
      // Find minimum year from all comparison data
      const years = comparisonData
        .map(item => item.plan_year)
        .filter(year => year != null);
      
      if (years.length > 0) {
        firstFilingYear = Math.min(...years);
      }
    }
    
    // Use the LATER (more recent) of the two dates - this is the actual start of our historical data
    if (planEffYear && firstFilingYear) {
      historicalStartYear = Math.max(planEffYear, firstFilingYear);
    } else if (firstFilingYear) {
      historicalStartYear = firstFilingYear;
    } else if (planEffYear) {
      historicalStartYear = planEffYear;
    }
    
    console.log('📅 Historical Analysis Period:', {
      planEffYear,
      firstFilingYear,
      historicalStartYear
    });
  } catch (error) {
    console.error('Error calculating historical start year:', error);
  }

  // Get scenario projections for future impact - Find ACTUAL lowest cost PROVIDER scenario
  const providerScenarios25yr = scenarioData?.scenarios?.filter(s => 
    s.projection_years === 25 && 
    s.scenario_type?.startsWith('PROVIDER_')
  ) || [];
  
  const best25YearData = providerScenarios25yr.reduce((best, current) => {
    const bestCost = best?.avg_alt_annual_fee || Infinity;
    const currentCost = current?.avg_alt_annual_fee || Infinity;
    return currentCost < bestCost ? current : best;
  }, null);

  const futureExcessFees = best25YearData?.projected_total_savings || 0;
  const futurePerParticipant = best25YearData?.projected_savings_per_participant || 0;
  
  // Calculate savings range for all provider scenarios
  const savingsAmounts = providerScenarios25yr
    .map(s => s.projected_total_savings)
    .filter(s => s > 0)
    .sort((a, b) => a - b);
  const minSavings = savingsAmounts[0] || 0;
  const maxSavings = savingsAmounts[savingsAmounts.length - 1] || 0;

  // Calculate peer ranking from API data instead of hardcoding 95th percentile
  const totalPeers = scatterData?.total_peers || 19845;
  const plansPayingMore = scatterData?.peers_costing_more || 0;
  const plansPayingLess = scatterData?.peers_costing_less || 0;

  // Export PDF handler - Waits for full render, then clones DOM to new window
  const handleExportPDF = async () => {
    setExporting(true);
    console.log('Generating PDF report via backend...');

    try {
      const response = await fetch(
        `${API_BASE_URL}/plan-analysis/${ein}/${planNumber}/${planYear}/pdf`
      );

      if (!response.ok) {
        throw new Error(`PDF generation failed: ${response.status}`);
      }

      const blob = await response.blob();

      // Create download link and trigger browser download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const disposition = response.headers.get('Content-Disposition');
      let filename = `401k-Fee-Analysis-${planData?.sponsor_name || 'plan'}-${planYear}.pdf`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('PDF downloaded:', filename);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle contact form submission
  const handleContactFormSubmit = async (contactInfo) => {
    // Close modal first
    setShowContactModal(false);
    
    // Try to submit to backend (optional - don't block on this)
    fetch(`${API_BASE_URL}/submit-export-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...contactInfo,
        plan_ein: planData.ein,
        plan_number: planData.plan_number,
        plan_year: planData.plan_year,
        timestamp: new Date().toISOString()
      })
    }).catch(error => {
      // Silently log error - endpoint may not exist yet
      console.log('Contact submission endpoint not available:', error.message);
    });

    // Generate PDF (don't wait for contact submission)
    handleExportPDF();
  };

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh' }}>
      {/* Modal Component */}
      {activeModal && (
        <Modal onClose={() => setActiveModal(null)}>
          {activeModal === 'yearByYear' && <YearByYearModal comparisonData={comparisonData} />}
          {activeModal === 'historical' && <HistoricalModal historyData={rawHistoryData} />}
          {activeModal === 'scenarios' && <ScenariosModal scenarioData={scenarioData} />}
          {activeModal === 'characteristics' && <CharacteristicsModal planData={planData} />}
        </Modal>
      )}

      {/* Contact Modal for PDF Export */}
      {showContactModal && (
        <ContactModal 
          onSubmit={handleContactFormSubmit}
          onClose={() => setShowContactModal(false)}
        />
      )}

      {/* Next Steps / Request More Information Modal */}
      {showNextSteps && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowNextSteps(false)}
              style={{
                position: 'sticky',
                top: '1rem',
                right: '1rem',
                float: 'right',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '50%',
                width: '2.5rem',
                height: '2.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                margin: '1rem'
              }}
            >
              <X size={18} />
            </button>
            <NextStepsTab 
              ein={ein}
              planNumber={planNumber}
              planYear={planYear}
              planDetails={planData}
            />
          </div>
        </div>
      )}

      {/* Top Navigation */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em' }}>
              Love Your 401k
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay for a 401(k)?</a>
              <a href="/faq" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>FAQ</a>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1.5rem 0'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          {/* Company Name Row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', lineHeight: 1.05 }}>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: '0.375rem' }}>
                <h1 style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: 700, 
                  color: '#111827', 
                  margin: 0,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.05
                }}>
                  {toProperCase(planData.sponsor_name)}
                </h1>
              </div>
              
              {/* Plan Name & Industry */}
              <div style={{ fontSize: '0.9375rem', color: '#6b7280', marginBottom: '0.375rem', lineHeight: 1.05 }}>
                {planData.plan_name ? toProperCase(planData.plan_name) : '401(K) Plan'} | {planData.business_description || 'Industry Not Specified'}
              </div>
              
              {/* Metadata Row */}
              <div style={{ 
                display: 'flex', 
                gap: '1.5rem', 
                fontSize: '0.8125rem', 
                color: '#9ca3af',
                flexWrap: 'wrap',
                alignItems: 'center',
                lineHeight: 1.05
              }}>
                <span>
                  <span style={{ color: '#6b7280' }}>EIN:</span> <strong style={{ color: '#6b7280' }}>{planData.ein}</strong>
                </span>
                <span>
                  <span style={{ color: '#6b7280' }}>Plan #:</span> <strong style={{ color: '#6b7280' }}>{planData.plan_number}</strong>
                </span>
                <span>
                  <span style={{ color: '#6b7280' }}>Effective:</span> <strong style={{ color: '#6b7280' }}>{planData.plan_eff_date || 'N/A'}</strong>
                </span>
                <button
                  onClick={() => setActiveModal('characteristics')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#1e3a8a',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: 0
                  }}
                >
                  Plan Characteristics
                  <ExternalLink size={12} />
                </button>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
              <button style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                color: '#374151',
                padding: '0.625rem 1.25rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Download size={16} />
                Save
              </button>
            </div>
          </div>
          
          {/* Quick Stats Bar */}
          <div style={{ 
            display: 'flex', 
            gap: '2.5rem',
            alignItems: 'baseline'
          }}>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                Most Recent Plan Year:{' '}
              </span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#6b7280' }}>
                {planData.plan_year}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                Total Assets:{' '}
              </span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#6b7280' }}>
                {formatCurrency(planData.assets)}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                Participants:{' '}
              </span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#6b7280' }}>
                {planData.participants || 0}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                Contributions:{' '}
              </span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#6b7280' }}>
                {formatCurrency(planData.total_contributions)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Other Expense Substitution Warning Banner */}
      {hasExpenseSubstitution && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '1rem 2.5rem 0' }}>
          <div style={{
            background: '#FEF3C7',
            border: '2px solid #F59E0B',
            borderRadius: '12px',
            padding: '1.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
              <div style={{ fontSize: '2rem' }}>&#x26A0;&#xFE0F;</div>
              <div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#92400E',
                  marginBottom: '0.5rem'
                }}>
                  Expense Data Note
                </h3>
                <p style={{ fontSize: '0.9375rem', color: '#78350F', lineHeight: 1.6 }}>
                  In {substitutedYears.length > 0 ? substitutedYears.join(' and ') : planData.plan_year}, this plan
                  reported $0 in administrative expenses but {formatCurrency(planData.other_expenses_raw)} in
                  "other expenses" — consistent with a common Form 5500 filing error pattern. The other expense
                  amount has been used for cost comparison in {substitutedYears.length > 1 ? 'these years' : 'this year'}.
                  Original Form 5500 data is unmodified.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
        
        {/* Section 1: Cost Comparison */}
        <section style={{ padding: '1.5rem 0 5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '35% 65%', gap: '5rem', alignItems: 'center' }}>
            {/* Left: Text Content */}
            <div>
              <h2 style={{
                fontSize: '2.5rem',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '1.25rem',
                letterSpacing: '-0.01em',
                lineHeight: 1.2
              }}>
                Your plan costs are {costMultiplier.toFixed(1)}× {isLowerCost ? 'lower' : 'higher'} than the industry median
              </h2>
              <p style={{
                fontSize: '1.125rem',
                color: '#6b7280',
                marginBottom: '2rem',
                lineHeight: 1.7
              }}>
                {isLowerCost 
                  ? "Your plan is achieving better value compared to similar companies in your industry."
                  : "Companies similar to yours are paying significantly less for their 401(k) plan administration."
                }
              </p>

              {/* Cost Cards */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: '0.875rem', 
                  color: '#6b7280', 
                  marginBottom: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Per Participant Cost
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Your Cost
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                      {formatCurrencyDetailed(costPerParticipant)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Peer Median
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a' }}>
                      {formatCurrencyDetailed(peerBenchmark)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                      {isLowerCost ? 'Savings on Fees' : 'Excess Fees Paid'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: isLowerCost ? '#16a34a' : '#dc2626' }}>
                      {formatCurrencyDetailed(absCostDifference)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Callout with link */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                  {isLowerCost ? (
                    <>
                      Out of {totalPeers.toLocaleString()} similar plans analyzed, only <strong>{plansPayingLess.toLocaleString()} plans</strong> pay less than you do. 
                      That means <strong>{plansPayingMore.toLocaleString()} companies pay more</strong> for their 401(k) plan than you do - giving your employees an opportunity to save more money each year.
                    </>
                  ) : (
                    <>
                      Out of {totalPeers.toLocaleString()} similar plans analyzed, only <strong>{plansPayingMore.toLocaleString()} plans</strong> pay more than you do. 
                      That means <strong>{plansPayingLess.toLocaleString()} companies pay less</strong> for their 401(k) plan than you do - giving their employees an opportunity to save more money each year.
                    </>
                  )}
                </div>
                <button
                  onClick={() => setActiveModal('yearByYear')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#1e3a8a',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline'
                  }}
                >
                  See year-by-year cost breakdown →
                </button>
              </div>
            </div>

            {/* Right: Scatter Plot */}
            <div style={{
              background: '#fafbfc',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              height: '400px'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '1.5rem', textAlign: 'center' }}>
                How Your Plan Compares (hover over each data point to see comparable firms)
              </div>
              <ScatterPlotVisualization scatterData={scatterData} costPerParticipant={costPerParticipant} />
            </div>
          </div>
        </section>

        {/* Section 2: Historical Impact */}
        <section style={{ padding: '5rem 0', background: 'white', marginLeft: '-2.5rem', marginRight: '-2.5rem', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '65% 35%', gap: '5rem', alignItems: 'center', marginBottom: '3rem' }}>
              {/* Left: Chart */}
              <div style={{
                background: '#fafbfc',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '1.5rem' }}>
                  Cost Per Participant Over Time
                </div>
                
                {historyData && historyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300} minHeight={300}>
                    <LineChart data={historyData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                      <XAxis 
                        dataKey="plan_year" 
                        tick={{ fill: '#737373', fontSize: 11 }}
                      />
                      <YAxis 
                        tick={{ fill: '#737373', fontSize: 11 }}
                        tickFormatter={formatCurrencyShort}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrencyShort(value)}
                        contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="peer_median_cost_per_participant"
                        stroke="#9ca3af"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Peer Median"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="company_cost_per_participant" 
                        stroke="#14b8a6" 
                        strokeWidth={3}
                        name="Your Plan"
                        dot={{ r: 4, fill: '#14b8a6' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                    Chart: Historical cost comparison
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
                      {isLowerCost ? 'Total Savings Achieved' : 'Total Opportunity Cost Of High Fees'}
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: isLowerCost ? '#16a34a' : '#dc2626' }}>
                      {formatCurrency(Math.abs(historicalLoss))}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{headlineWindowLabel ? headlineWindowLabel.toLowerCase() : `since ${historicalStartYear}`}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.5rem' }}>Per Participant</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>
                      {formatCurrency(Math.abs(historicalPerParticipant))}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {isLowerCost ? 'savings achieved' : 'missed opportunity'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Text */}
              <div>
                <h2 style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: '1.25rem',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em'
                }}>
                  Historical Impact Analysis
                </h2>
                <p style={{
                  fontSize: '3.5rem',
                  fontWeight: 900,
                  color: isLowerCost ? '#16a34a' : '#1e3a8a',
                  marginBottom: '0.5rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1
                }}>
                  {formatCurrency(Math.abs(historicalLoss))}
                </p>
                <p style={{
                  fontSize: '1.125rem',
                  color: '#6b7280',
                  marginBottom: '1.5rem',
                  lineHeight: 1.7
                }}>
                  {isLowerCost
                    ? `total savings vs peer median (${headlineWindowLabel || `since ${historicalStartYear}`})`
                    : `total excess fees vs peer median (${headlineWindowLabel || `since ${historicalStartYear}`})`}
                </p>
                <button
                  onClick={() => setActiveModal('historical')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#1e3a8a',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                    marginBottom: '1.5rem'
                  }}
                >
                  See detailed historical data and performance charts →
                </button>
                
                {peerLookbackData && peerLookbackData.headline && lookbackDisplayMode !== 'hide' ? (() => {
                  // Use peer-median lookback for the detail paragraph too.
                  // actualBalance = the plan's most recent EOY (from the lookback)
                  // potentialBalance = what it would be if peer-median fees applied,
                  //                    i.e. actual + total_economic_impact (compounded).
                  const actualBalance = lookbackFinalEoy || planData.total_assets || 0;
                  const ppts = lookbackFinalParticipants || planData.participants || 1;
                  const missedOpportunity = historicalLoss;            // signed: + = overpaid
                  const potentialBalance = actualBalance + missedOpportunity;
                  const isHistoricalSavings = missedOpportunity < 0;
                  const absOpportunity = Math.abs(missedOpportunity);
                  const absPerParticipant = absOpportunity / ppts;
                  const actualPerParticipant = actualBalance / ppts;
                  const potentialPerParticipant = potentialBalance / ppts;
                  const windowLabel = headlineWindowLabel || `since ${historicalStartYear}`;
                  const isSimpleSumOnly = lookbackDisplayMode === 'simple_sum';

                  return (
                    <div style={{
                      background: '#f3f4f6',
                      padding: '1.25rem',
                      borderRadius: '8px',
                      fontSize: '0.9375rem',
                      color: '#374151',
                      lineHeight: 1.6
                    }}>
                      {isHistoricalSavings ? (
                        <>
                          <p style={{ marginBottom: '0.75rem' }}>
                            Your plan currently has total assets of <strong>{formatCurrency(actualBalance)}</strong>.
                            Compared to the typical peer plan in your industry and size bracket
                            ({windowLabel.toLowerCase()}), your lower fees have preserved
                            <strong style={{ color: '#16a34a' }}> {formatCurrency(absOpportunity)}</strong> in plan assets.
                          </p>
                          <p>
                            Each participant has an average balance of <strong>{formatCurrency(actualPerParticipant)}</strong>.
                            At peer-median fees they would have had <strong>{formatCurrency(potentialPerParticipant)}</strong> —
                            your lower fees preserved <strong style={{ color: '#16a34a' }}>{formatCurrency(absPerParticipant)} per person</strong>.
                          </p>
                        </>
                      ) : (
                        <>
                          <p style={{ marginBottom: '0.75rem' }}>
                            Your plan currently has total assets of <strong>{formatCurrency(actualBalance)}</strong>.
                            At peer-median fees ({windowLabel.toLowerCase()}) it would have
                            <strong> {formatCurrency(potentialBalance)}</strong> —
                            a difference of <strong style={{ color: '#dc2626' }}>{formatCurrency(absOpportunity)}</strong>.
                          </p>
                          <p>
                            Each participant currently has an average balance of <strong>{formatCurrency(actualPerParticipant)}</strong>.
                            At peer-median fees they could have had <strong>{formatCurrency(potentialPerParticipant)}</strong> —
                            an increase of <strong style={{ color: '#dc2626' }}>{formatCurrency(absPerParticipant)} per person</strong>.
                          </p>
                        </>
                      )}
                      {isSimpleSumOnly && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#6b7280', fontStyle: 'italic' }}>
                          Note: peer benchmarks were unavailable for some years in this plan's history.
                          The figure above is the simple sum of annual differences without compounding.
                        </p>
                      )}
                    </div>
                  );
                })() : (
                  <div style={{
                    background: '#f3f4f6',
                    padding: '1.25rem',
                    borderRadius: '8px',
                    fontSize: '0.9375rem',
                    color: '#374151',
                    lineHeight: 1.6
                  }}>
                    {isLowerCost ? (
                      <>Your lower costs have saved participants <strong style={{ color: '#16a34a' }}>{formatCurrency(Math.abs(historicalLoss / (planData.participants || 1)))} per person</strong> compared to peer-median costs.</>
                    ) : (
                      <>At peer-median fees, participants would have an additional <strong>{formatCurrency(historicalLoss / (planData.participants || 1))} per person</strong>.</>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Your Contributions Going to Fees */}
        <section style={{ padding: '5rem 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '35% 65%', gap: '5rem', alignItems: 'center' }}>
            {/* Left: Text Content */}
            <div>
              <h2 style={{
                fontSize: '2.5rem',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '1.25rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}>
                Your Annual Contributions
              </h2>
              <p style={{
                fontSize: '1.125rem',
                color: '#6b7280',
                lineHeight: 1.7,
                marginBottom: '2rem'
              }}>
                {isLowerCost ? (
                  <>
                    Your plan is efficiently managing costs, allowing more of your contributions to grow over time.
                    {contributionCostData && contributionCostData.length > 0 && (() => {
                      const mostRecentYear = contributionCostData[0];
                      const percentage = mostRecentYear.admin_cost_as_pct_of_total_contrib;
                      return percentage !== null && percentage !== undefined ? (
                        <> In {mostRecentYear.plan_year}, only <strong>{percentage.toFixed(2)}%</strong> of Participant & Employer Contributions went to Fees — well below the industry median.</>
                      ) : null;
                    })()}
                  </>
                ) : (
                  <>
                    A significant portion of your contributions is being consumed by administrative costs before it can even start growing.
                    {contributionCostData && contributionCostData.length > 0 && (() => {
                      const mostRecentYear = contributionCostData[0];
                      const percentage = mostRecentYear.admin_cost_as_pct_of_total_contrib;
                      return percentage !== null && percentage !== undefined ? (
                        <> In {mostRecentYear.plan_year}, <strong>{percentage.toFixed(2)}%</strong> of Participant & Employer Contributions was consumed by Fees.</>
                      ) : null;
                    })()}
                  </>
                )}
              </p>
            </div>

            {/* Right: Charts */}
            <div>
              {/* Bar Chart: Admin Costs as % of Contributions */}
              <div style={{
                background: '#f9fafb',
                padding: '2rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                  Admin Costs as % of Contributions
                </div>
                {contributionCostData && contributionCostData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180} minHeight={180}>
                    <BarChart data={[...contributionCostData].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                      <XAxis 
                        dataKey="plan_year" 
                        tick={{ fill: '#737373', fontSize: 11 }}
                      />
                      <YAxis 
                        tick={{ fill: '#737373', fontSize: 11 }}
                        tickFormatter={(value) => `${value.toFixed(1)}%`}
                      />
                      <Tooltip 
                        formatter={(value) => `${value.toFixed(2)}%`}
                        contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Bar 
                        dataKey="admin_cost_as_pct_of_total_contrib" 
                        fill="#FF7A5C" 
                        name="Admin Cost %"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                    Bar chart: 2009-2024
                  </div>
                )}
              </div>

              {/* Line Chart: Admin Costs as % of Assets */}
              <div style={{
                background: '#f9fafb',
                padding: '2rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                  Admin Costs as % of Plan Assets
                </div>
                {contributionCostData && contributionCostData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180} minHeight={180}>
                    <LineChart data={[...contributionCostData].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                      <XAxis 
                        dataKey="plan_year" 
                        tick={{ fill: '#737373', fontSize: 11 }}
                      />
                      <YAxis 
                        tick={{ fill: '#737373', fontSize: 11 }}
                        tickFormatter={(value) => `${value.toFixed(2)}%`}
                        domain={[0, 'auto']}
                      />
                      <Tooltip 
                        formatter={(value) => `${value.toFixed(2)}%`}
                        contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="admin_cost_as_pct_of_assets" 
                        stroke="#7C5CDB" 
                        strokeWidth={3}
                        name="Admin Costs as % of Assets"
                        dot={{ r: 3, fill: '#7C5CDB' }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                    Line chart: 2009-2024
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Future Impact - Show for ALL plans with different messaging */}
        <section style={{ padding: '5rem 0', background: 'white', marginLeft: '-2.5rem', marginRight: '-2.5rem', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '35% 65%', gap: '5rem', alignItems: 'center', marginBottom: '3rem' }}>
              {/* Left: Text Content */}
              <div>
                <h2 style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: '1.25rem',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2
                }}>
                  Future Impact Analysis
                </h2>
                
                {isLowerCost ? (
                  // LOW-COST PLAN: Show WEALTH ADVANTAGE
                  <>
                    <p style={{
                      fontSize: '1.125rem',
                      color: '#059669',
                      marginBottom: '2.5rem',
                      lineHeight: 1.7,
                      fontWeight: 600
                    }}>
                      Your low-cost fee structure will grow your participants' wealth over time
                    </p>

                    <div style={{ marginBottom: '2.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#059669', marginBottom: '0.75rem', fontWeight: 500 }}>
                        Future Wealth Advantage
                      </div>
                      <div style={{ fontSize: '3.5rem', fontWeight: 900, color: '#059669', marginBottom: '0.5rem', letterSpacing: '-0.02em', lineHeight: 1 }}>
                        +{formatCurrency(Math.abs(futureExcessFees))}
                      </div>
                      <div style={{ fontSize: '1.125rem', color: '#111827', fontWeight: 600 }}>
                        +{formatCurrency(Math.abs(futurePerParticipant))} Per Participant
                      </div>
                    </div>

                    <div style={{
                      fontSize: '1rem',
                      color: '#374151',
                      lineHeight: 1.8,
                      marginBottom: '1.5rem'
                    }}>
                      By maintaining your low-cost fee structure, your participants will accumulate significantly more wealth over 25 years compared to peers paying average fees. This additional money compounds in their retirement accounts instead of going to administrative fees.
                    </div>
                    
                    <div style={{
                      padding: '1rem',
                      background: '#D1FAE5',
                      borderRadius: '8px',
                      border: '1px solid #10B981'
                    }}>
                      <div style={{ fontSize: '0.875rem', color: '#065F46', lineHeight: 1.6 }}>
                        💡 <strong>Your Competitive Advantage:</strong> You're saving <strong>{formatCurrency(Math.abs(excessCost))}/participant/year</strong> compared to the typical peer plan. That's <strong>{Math.round((Math.abs(excessCost) / (peerBenchmark || 1)) * 100)}% less</strong> than the industry median!
                      </div>
                    </div>
                  </>
                ) : (
                  // HIGH-COST PLAN: Show LOSSES
                  <>
                    <p style={{
                      fontSize: '1.125rem',
                      color: '#6b7280',
                      marginBottom: '2.5rem',
                      lineHeight: 1.7
                    }}>
                      Over the next 25 years, your plan fees will further erode your savings
                    </p>

                    <div style={{ marginBottom: '2.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem', fontWeight: 500 }}>
                        Future Excess Fees
                      </div>
                      <div style={{ fontSize: '3.5rem', fontWeight: 900, color: '#DC2626', marginBottom: '0.5rem', letterSpacing: '-0.02em', lineHeight: 1 }}>
                        {formatCurrency(futureExcessFees)}
                      </div>
                      <div style={{ fontSize: '1.125rem', color: '#111827', fontWeight: 600 }}>
                        {formatCurrency(futurePerParticipant)} Per Participant
                      </div>
                    </div>

                    <div style={{
                      fontSize: '1rem',
                      color: '#374151',
                      lineHeight: 1.8
                    }}>
                      Every year you delay switching to a lower-cost provider, your participants lose not just that year's fee savings, but also all the compound growth those savings would have generated over the remaining years until retirement.
                    </div>
                  </>
                )}
              </div>

              {/* Right: Chart */}
              <div style={{
                background: '#fafbfc',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                    Projected Balance Growth Comparison
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', background: '#f3f4f6', padding: '0.375rem 0.75rem', borderRadius: '6px' }}>
                    Assumed return rate: {(() => {
                      const scenarios = scenarioData?.scenarios || (Array.isArray(scenarioData) ? scenarioData : null);
                      return scenarios?.[0]?.assumed_return_rate ? (scenarios[0].assumed_return_rate * 100).toFixed(1) : '7.0';
                    })()}%
                  </div>
                </div>
                
                {(() => {
                  const scenarios = scenarioData?.scenarios || (Array.isArray(scenarioData) ? scenarioData : null);
                  console.log('🎯 Chart Render Check:', {
                    hasScenarioData: !!scenarioData,
                    scenarios: scenarios,
                    scenariosLength: scenarios?.length
                  });
                  
                  return scenarios && scenarios.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300} minHeight={300}>
                      <LineChart data={(() => {
                        // Prepare chart data
                        const timeframes = [5, 10, 15, 20, 25];
                        return timeframes.map(years => {
                          const dataPoint = { years };
                          
                          // Find current plan data
                          const currentScenario = scenarios.find(
                            s => s.projection_years === years
                          );
                          if (currentScenario) {
                            dataPoint['Current Plan'] = currentScenario.projected_balance_current_costs;
                          }
                          
                          // Find best provider scenario (flat fee)
                          const flatFeeScenario = scenarios.find(
                            s => s.projection_years === years && 
                            (s.scenario_type === 'PROVIDER_FLAT_FEE_PRTCP_ONLY' || 
                             s.scenario_type?.includes('FLAT_FEE'))
                          );
                          if (flatFeeScenario) {
                            dataPoint['Flat Fee Per Participant'] = flatFeeScenario.projected_balance_alt_costs;
                          }
                          
                          console.log(`📊 Year ${years}:`, dataPoint);
                          return dataPoint;
                        });
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="years" 
                          label={{ value: 'Years from Now', position: 'insideBottom', offset: -5, fill: '#737373', fontSize: 11 }}
                          tick={{ fill: '#737373', fontSize: 11 }}
                        />
                        <YAxis 
                          label={{ value: 'Projected Balance ($)', angle: -90, position: 'insideLeft', fill: '#737373', fontSize: 11 }}
                          tickFormatter={(value) => formatCompact(value)}
                          tick={{ fill: '#737373', fontSize: 11 }}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          labelFormatter={(label) => `${label} Years`}
                          contentStyle={{ 
                            background: 'white', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Current Plan" 
                          stroke={isLowerCost ? "#10b981" : "#f87171"}
                          strokeWidth={3}
                          strokeDasharray={isLowerCost ? "0" : "5 5"}
                          dot={{ r: 4, fill: isLowerCost ? "#10b981" : "#f87171" }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Flat Fee Per Participant" 
                          stroke={isLowerCost ? "#f87171" : "#10b981"}
                          strokeWidth={3}
                          strokeDasharray={isLowerCost ? "5 5" : "0"}
                          dot={{ r: 4, fill: isLowerCost ? "#f87171" : "#10b981" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>

                    <div style={{ 
                      display: 'flex', 
                      gap: '2rem', 
                      justifyContent: 'center',
                      marginTop: '1rem',
                      fontSize: '0.8125rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ 
                          width: '20px', 
                          height: '3px', 
                          background: isLowerCost ? '#10b981' : '#f87171',
                          borderRadius: '2px',
                          backgroundImage: isLowerCost ? 'none' : 'repeating-linear-gradient(90deg, #f87171 0, #f87171 5px, transparent 5px, transparent 10px)'
                        }} />
                        <span style={{ color: isLowerCost ? '#10b981' : '#f87171', fontWeight: 500 }}>
                          {isLowerCost ? 'Your Low-Cost Plan' : 'Current Plan'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ 
                          width: '20px', 
                          height: '3px', 
                          background: isLowerCost ? '#f87171' : '#10b981',
                          borderRadius: '2px',
                          backgroundImage: isLowerCost ? 'repeating-linear-gradient(90deg, #f87171 0, #f87171 5px, transparent 5px, transparent 10px)' : 'none'
                        }} />
                        <span style={{ color: isLowerCost ? '#f87171' : '#10b981', fontWeight: 500 }}>
                          {isLowerCost ? 'If You Paid Peer Median' : 'Flat Fee Per Participant'}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                    Chart: 25-year projection
                  </div>
                );
                })()}
              </div>
            </div>

            {/* Scenarios Preview */}
            <div style={{ marginTop: '2rem' }}>
              <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6 }}>
                <strong style={{ color: '#111827' }}>Detailed Scenario Comparison:</strong> {isLowerCost 
                  ? `Your low-cost structure gives you a 25-year wealth advantage ranging from ${formatCompact(Math.abs(maxSavings))} to ${formatCompact(Math.abs(minSavings))} compared to higher-cost alternatives.`
                  : `Depending on the type of plan you choose, your 25-year savings range from ${formatCompact(minSavings)} to ${formatCompact(maxSavings)}.`
                }{' '}
                <button
                  onClick={() => setActiveModal('scenarios')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#1e3a8a',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline'
                  }}
                >
                  Show alternative fee scenarios →
                </button>
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Next Steps */}
        <section style={{ padding: '5rem 0 6rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '35% 65%', gap: '5rem', alignItems: 'center' }}>
            {/* Left: Text */}
            <div>
              <h2 style={{
                fontSize: '2.5rem',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '1.25rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}>
                Ready to Take Action?
              </h2>
              <p style={{
                fontSize: '1.125rem',
                color: '#6b7280',
                lineHeight: 1.7
              }}>
                Get a comprehensive analysis with alternative 401(k) fee structures and detailed savings projections tailored to your plan.
              </p>
            </div>

            {/* Right: Links */}
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <button
                  onClick={() => setShowContactModal(true)}
                  disabled={exporting}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: exporting ? '#9ca3af' : '#1e3a8a',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '1rem 0',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: exporting ? 'not-allowed' : 'pointer',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  <Download size={20} />
                  {exporting ? 'Generating PDF...' : 'Download Full Report (PDF)'}
                  <ArrowRight size={20} style={{ marginLeft: 'auto' }} />
                </button>
                <button
                  onClick={() => setShowNextSteps(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#1e3a8a',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '1rem 0',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  Request More Information on Plans
                  <ArrowRight size={20} style={{ marginLeft: 'auto' }} />
                </button>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div style={{
            marginTop: '3rem',
            padding: '1.5rem 0',
            borderTop: '1px solid #e5e7eb'
          }}>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.6, margin: 0, textAlign: 'center' }}>
              <strong style={{ color: '#111827' }}>What's included:</strong> Asset growth charts, participant trends, complete fee structure analysis, and alternative 401(k) pricing structures.
            </p>
          </div>
        </section>

        {/* Hidden PDF Report Component */}
        <PlanAnalysisReport 
          planData={planData}
          comparisonData={comparisonData}
          historyData={historyData}
          rawHistoryData={rawHistoryData}
          scatterData={scatterData}
          scenarioData={scenarioData}
          contributionCostData={contributionCostData}
          lookbackData={lookbackData}
        />

      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ children, onClose }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'sticky',
            top: '1rem',
            right: '1rem',
            float: 'right',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '50%',
            width: '2rem',
            height: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10
          }}
        >
          <X size={16} />
        </button>
        <div style={{ padding: '2rem' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Year By Year Modal
const YearByYearModal = ({ comparisonData }) => {
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Year-by-Year Breakdown (Average Cost Per Participant)</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Year</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Your Cost</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Peer Median</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Difference</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Similar Plans</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((year, idx) => {
              const peerMedianYear = year.peer_median_cost_per_participant || 0;
              const diff = (year.company_cost_per_participant || 0) - peerMedianYear;
              const diffColor = diff >= 0 ? '#dc2626' : '#16a34a';
              const diffPrefix = diff >= 0 ? '+' : '';
              return (
              <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{year.plan_year}</td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>
                  {formatCurrency(year.company_cost_per_participant)}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', color: '#1e3a8a' }}>
                  {formatCurrency(peerMedianYear)}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', color: diffColor, fontWeight: 600 }}>
                  {diffPrefix}{formatCurrency(diff)}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem' }}>
                  {year.peer_plan_count?.toLocaleString() || 'N/A'}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Historical Modal
const HistoricalModal = ({ historyData }) => {
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Debug: log the data structure
  console.log('📊 History Data in Modal:', historyData);

  if (!historyData || historyData.length === 0) {
    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Complete Historical Data</h2>
        <p>No historical data available.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Complete Historical Data</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Year</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Participants</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Assets</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Contributions</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Admin Cost</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>Cost/Participant</th>
            </tr>
          </thead>
          <tbody>
            {historyData.map((year, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{year.plan_year || year.year}</td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem' }}>
                  {year.participants !== null && year.participants !== undefined ? year.participants.toLocaleString() : 'N/A'}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem' }}>
                  {formatCurrency(year.assets || year.total_assets)}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem' }}>
                  {formatCurrency(year.contributions || year.total_contributions)}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem' }}>
                  {formatCurrency(year.admin_expenses || year.total_admin_expenses)}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>
                  {formatCurrency(year.cost_per_participant)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Scenarios Modal
const ScenariosModal = ({ scenarioData }) => {
  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCompact = (value) => {
    if (!value && value !== 0) return 'N/A';
    if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + 'M';
    }
    if (value >= 1000) {
      return '$' + (value / 1000).toFixed(0) + 'K';
    }
    return formatCurrency(value);
  };

  if (!scenarioData || !scenarioData.scenarios) {
    return <div>No scenario data available</div>;
  }

  // Scenario configuration with names and colors
  const getScenarioConfig = (scenarioType) => {
    const configs = {
      'PROVIDER_FLAT_FEE_PRTCP_ONLY': { name: 'Flat Fee Per Participant', description: 'Flat fee per participant only', color: '#10b981', priority: 1 },
      'PROVIDER_FLAT_FEE_P+AUA': { name: 'Flat Fee + AUA', description: 'Flat fee per participant + asset-based', color: '#0E8376', priority: 2 },
      'PROVIDER_FLAT_FEE_ADMIN+P': { name: 'Flat Fee Admin + Participant', description: 'Base admin fee + per participant', color: '#33BFAF', priority: 3 },
      'PROVIDER_BENCHMARK': { name: 'Benchmark Provider', description: 'Generic low-cost option', color: '#12A493', priority: 4 },
      'PROVIDER_MID_STD': { name: 'Industry Standard', description: 'Mid-market pricing', color: '#7C5CDB', priority: 5 },
      'PROVIDER_HIGH_TOUCH': { name: 'High Touch Service', description: 'Premium service model', color: '#f59e0b', priority: 6 },
      'PROVIDER_HIGH_ASSET_FEES75BPS': { name: 'High Asset Fees75bps', description: 'PROVIDER_HIGH_ASSET_FEES75BPS', color: '#ef4444', priority: 7 }
    };
    return configs[scenarioType] || { name: scenarioType, description: '', color: '#6b7280', priority: 99 };
  };

  // Group scenarios by type (filter to only PROVIDER_ scenarios)
  const scenariosByType = {};
  scenarioData.scenarios.forEach(scenario => {
    if (scenario.scenario_type?.startsWith('PROVIDER_')) {
      if (!scenariosByType[scenario.scenario_type]) {
        scenariosByType[scenario.scenario_type] = [];
      }
      scenariosByType[scenario.scenario_type].push(scenario);
    }
  });

  // Sort scenario types by priority
  const sortedScenarioTypes = Object.keys(scenariosByType).sort((a, b) => {
    return (getScenarioConfig(a)?.priority || 99) - (getScenarioConfig(b)?.priority || 99);
  });

  // Show only top 6
  const displayedScenarios = sortedScenarioTypes.slice(0, 6);

  // Find best scenario for current plan data
  const bestScenario = sortedScenarioTypes[0];

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Detailed Scenario Comparison</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>Scenario</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>Annual Cost</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>5 Years</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>10 Years</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>15 Years</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>20 Years</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>25 Years</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>Total Savings</th>
            </tr>
          </thead>
          <tbody>
            {/* Current Plan Row */}
            <tr style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
              <td style={{ padding: '1rem' }}>
                <div style={{ fontWeight: 600, color: '#111827' }}>Current Plan</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Your existing costs</div>
              </td>
              <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>
                {scenarioData?.current_costs?.current_annual_admin_cost !== undefined 
                  ? formatCurrency(scenarioData.current_costs.current_annual_admin_cost)
                  : 'N/A'}
              </td>
              {[5, 10, 15, 20, 25].map(years => {
                const data = scenariosByType[bestScenario]?.find(s => s.projection_years === years);
                return (
                  <td key={years} style={{ padding: '1rem', textAlign: 'right', color: '#4b5563', fontWeight: 500 }}>
                    {data && formatCompact(data.projected_balance_current_costs)}
                  </td>
                );
              })}
              <td style={{ padding: '1rem', textAlign: 'right', color: '#9ca3af' }}>—</td>
            </tr>

            {/* Alternative Scenarios */}
            {displayedScenarios.map(scenarioType => {
              const config = getScenarioConfig(scenarioType);
              const scenarios = scenariosByType[scenarioType] || [];
              const data25yr = scenarios.find(s => s.projection_years === 25);
              
              return (
                <tr key={scenarioType} style={{ borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 600, color: config.color }}>{config.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{config.description}</div>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, color: '#111827' }}>
                    {data25yr && formatCurrency(data25yr.avg_alt_annual_fee)}
                  </td>
                  {[5, 10, 15, 20, 25].map(years => {
                    const data = scenarios.find(s => s.projection_years === years);
                    return (
                      <td key={years} style={{ padding: '1rem', textAlign: 'right', color: '#374151', fontWeight: 500 }}>
                        {data && formatCompact(data.projected_balance_alt_costs)}
                      </td>
                    );
                  })}
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    {data25yr && (
                      <div>
                        <div style={{ fontWeight: 700, color: '#10b981', fontSize: '0.875rem' }}>
                          {formatCompact(data25yr.projected_total_savings)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {formatCurrency(data25yr.projected_savings_per_participant)}/person
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Characteristics Modal
const CharacteristicsModal = ({ planData }) => {
  const characteristics = planData.plan_characteristics;
  
  if (!characteristics) {
    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Plan Characteristics</h2>
        <p>No plan characteristics data available.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Plan Characteristics</h2>
      
      {planData.business_description && (
        <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #86efac' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#166534', marginBottom: '0.25rem' }}>INDUSTRY</div>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: '#15803d' }}>{planData.business_description}</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        {characteristics.plan_category && (
          <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Plan Category</div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1e3a8a' }}>{characteristics.plan_category}</div>
          </div>
        )}
        
        {characteristics.plan_subtype && (
          <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Plan Subtype</div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1e3a8a' }}>{characteristics.plan_subtype}</div>
          </div>
        )}
      </div>

      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Key Features</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {characteristics.has_auto_enrollment !== null && (
          <FeatureBadge label="Auto-Enrollment" value={characteristics.has_auto_enrollment} />
        )}
        {characteristics.has_matching !== null && (
          <FeatureBadge label="Employer Match" value={characteristics.has_matching} />
        )}
        {characteristics.is_participant_directed !== null && (
          <FeatureBadge label="Self-Directed" value={characteristics.is_participant_directed} />
        )}
        {characteristics.has_default_investment !== null && (
          <FeatureBadge label="Default Investment" value={characteristics.has_default_investment} />
        )}
        {characteristics.has_brokerage_option !== null && (
          <FeatureBadge label="Brokerage Window" value={characteristics.has_brokerage_option} />
        )}
        {characteristics.likely_safe_harbor !== null && (
          <FeatureBadge label={`Safe Harbor${characteristics.safe_harbor_confidence ? ` (${characteristics.safe_harbor_confidence})` : ''}`} value={characteristics.likely_safe_harbor} />
        )}
      </div>

      {/* Additional Attributes */}
      {(characteristics.is_controlled_group !== null || characteristics.is_frozen_plan !== null) && (
        <>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Additional Attributes</h3>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {characteristics.is_controlled_group !== null && (
              <div style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fafbfc', fontSize: '0.875rem', fontWeight: 600 }}>
                Controlled Group: {characteristics.is_controlled_group ? 'Yes' : 'No'}
              </div>
            )}
            {characteristics.is_frozen_plan !== null && (
              <div style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fafbfc', fontSize: '0.875rem', fontWeight: 600 }}>
                Frozen Plan: {characteristics.is_frozen_plan ? 'Yes' : 'No'}
              </div>
            )}
          </div>
        </>
      )}

      {/* Detailed Plan Features from Pension Codes */}
      {characteristics.pension_code_descriptions && characteristics.pension_code_descriptions.length > 0 && (
        <>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Detailed Plan Features</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {characteristics.pension_code_descriptions.map((code, idx) => (
              <div key={idx} style={{ background: '#fafbfc', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#166534', background: '#d1fae5', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                    {code.code}
                  </span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.25rem' }}>
                      {code.category}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#111827' }}>
                      {code.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FeatureBadge = ({ label, value }) => {
  return (
    <div style={{
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid',
      borderColor: value ? '#86efac' : '#e5e7eb',
      background: value ? '#f0fdf4' : '#fafbfc'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        {value ? (
          <svg style={{ width: '1rem', height: '1rem', color: '#16a34a' }} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg style={{ width: '1rem', height: '1rem', color: '#9ca3af' }} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )}
        <span style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: value ? '#166534' : '#6b7280'
        }}>
          {label}
        </span>
      </div>
    </div>
  );
};

// Scatter Plot Visualization with real scatter chart
const ScatterPlotVisualization = ({ scatterData, costPerParticipant }) => {
  const chartRef = useRef(null);
  
  if (!scatterData) {
    return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
      Scatter plot visualization
    </div>;
  }

  // Prepare Chart.js data
  const peerDataset = {
    label: 'Peer Plans',
    data: (scatterData.peer_companies || []).map(peer => ({
      x: peer.participants_with_balance,
      y: peer.cost_per_participant,
      name: peer.name,
      ein: peer.ein,
      assets: peer.assets
    })),
    backgroundColor: 'rgba(107, 114, 128, 0.6)',
    borderColor: 'rgba(107, 114, 128, 0.8)',
    borderWidth: 1,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointHoverBorderWidth: 2
  };

  const targetDataset = scatterData.target_company ? {
    label: 'Your Plan',
    data: [{
      x: scatterData.target_company.participants_with_balance,
      y: scatterData.target_company.cost_per_participant,
      name: scatterData.target_company.name,
      ein: scatterData.target_company.ein,
      assets: scatterData.target_company.assets,
      isTarget: true
    }],
    backgroundColor: 'rgba(220, 38, 38, 0.9)',
    borderColor: '#B91C1C',
    borderWidth: 3,
    pointRadius: 10,
    pointHoverRadius: 12,
    pointStyle: 'circle',
    pointBorderWidth: 3
  } : null;

  const datasets = [peerDataset];
  if (targetDataset) datasets.push(targetDataset);

  const chartData = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 12, weight: '600' },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'white',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#e5e7eb',
        borderWidth: 2,
        padding: 12,
        displayColors: false,
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          title: (items) => items[0].raw.name || 'Company',
          label: (context) => {
            const point = context.raw;
            const labels = [
              `Cost: ${formatCurrencyShort(point.y)}/participant`,
              `Participants: ${point.x.toLocaleString()}`
            ];
            if (point.assets > 0) {
              labels.push(`Assets: ${formatCurrencyShort(point.assets)}`);
            }
            return labels;
          },
          afterLabel: (context) => {
            const point = context.raw;
            return point.isTarget ? '\n★ YOUR PLAN' : '';
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Participants with Balance',
          font: { size: 12, weight: '600' },
          color: '#737373'
        },
        ticks: {
          color: '#737373',
          font: { size: 11 }
        },
        grid: {
          color: '#E5E5E5',
          drawTicks: true
        }
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: 'Cost per Participant',
          font: { size: 12, weight: '600' },
          color: '#737373'
        },
        ticks: {
          color: '#737373',
          font: { size: 11 },
          callback: (value) => formatCurrencyShort(value)
        },
        grid: {
          color: '#E5E5E5',
          drawTicks: true
        }
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: true
    }
  };

  return (
    <div style={{ height: '100%', position: 'relative' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem', textAlign: 'center' }}>
        Cost Distribution vs Peer Group ({scatterData.plan_year || 'Latest'})
      </div>
      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '1rem', textAlign: 'center' }}>
        {scatterData.size_range} | {scatterData.total_peers.toLocaleString()} similar plans
      </div>
      
      {scatterData.peer_companies && scatterData.peer_companies.length > 0 ? (
        <div style={{ width: '100%', height: '450px', position: 'relative' }}>
          <Scatter
            ref={chartRef}
            data={chartData}
            options={options}
          />
        </div>
      ) : (
        <div style={{ height: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
          No peer data available
        </div>
      )}
    </div>
  );
};

// Contact Modal for PDF Export
const ContactModal = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', color: '#111827' }}>
          Request Full Report
        </h2>
        <p style={{ fontSize: '0.9375rem', color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.6 }}>
          Please provide your contact information to download the complete analysis report including all charts, tables, and recommendations.
        </p>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
              Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
              Company
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '0.875rem',
                background: '#1e3a8a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#1e40af'}
              onMouseLeave={(e) => e.target.style.background = '#1e3a8a'}
            >
              Download Report
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.875rem 1.5rem',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
              onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanAnalysisScroll;
// Cache buster 
// Cache buster Tue 12/30/2025 15:35:16.02 
// Build timestamp: Thu 02/12/2026 17:35:27.80 
